'use client';

import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';

import { FeatureProductTileBase } from '@/fo/feature/product/tile/base';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitPaginationBase } from '@/fo/ui/kit/pagination/base';
import { MappedProduct } from '@/fo/util/map-product-data';

import { styles } from './ProductsList.styles';

export interface ProductsListProps {
  products: MappedProduct[];
  isAuthorized: boolean;
  catalogUrl?: string;
}

const PAGE_SIZE = 36;

export function ProductsList({
  products,
  isAuthorized,
  catalogUrl = '/catalog',
  ...props
}: ProductsListProps) {
  const t = useTranslations();
  const slots = styles();
  const [pagination, setPagination] = useState<{
    page: number;
    pageSize: number;
    count: number;
    loadMoreFromPage: number | null;
  }>({
    page: 1,
    pageSize: PAGE_SIZE,
    count: Math.ceil(products.length / PAGE_SIZE),
    loadMoreFromPage: null
  });

  const productsList = useMemo(() => {
    const { page, pageSize, loadMoreFromPage } = pagination;

    if (loadMoreFromPage) {
      return products.slice((loadMoreFromPage - 1) * pageSize, page * pageSize);
    }

    return products.slice((page - 1) * pageSize, page * pageSize);
  }, [products, pagination]);

  const onLoadMore = () => {
    setPagination((prev) => ({
      ...prev,
      page: prev.page + 1,
      loadMoreFromPage: prev.loadMoreFromPage ?? prev.page
    }));
  };

  const catalogHref = catalogUrl.startsWith('/')
    ? catalogUrl
    : `/${catalogUrl}`;

  return (
    <div
      className={slots.base()}
      {...props}
    >
      {Boolean(productsList.length) && (
        <div className={slots.products()}>
          {productsList.map((productData: MappedProduct, index: number) => (
            <FeatureProductTileBase
              key={`${productData.sku}-${index}`}
              isAuthorized={isAuthorized}
              data={productData}
              enableMicrodata
              listName="actions"
              positionIndex={index}
            />
          ))}
        </div>
      )}
      {pagination.count > 1 && (
        <div className={slots.pagination()}>
          <UiKitPaginationBase
            count={pagination.count}
            page={pagination.page}
            onChange={(page) =>
              setPagination({ ...pagination, page, loadMoreFromPage: null })
            }
            disabledNavigation
            onLoadMore={onLoadMore}
          />
        </div>
      )}
      <UiKitButtonBase
        className={slots.button()}
        as="link"
        href={catalogHref}
      >
        {t('goToCatalog')}
      </UiKitButtonBase>
    </div>
  );
}
